/**
 * Admin › Configurações › **Atendimento e IA** (D631, Digital Central phase 0, doc 25 section 11) - the pure view model behind
 * `CentralAiSettingsScreen`.
 *
 * Seven governed keys of the settings kernel (D393), grouped by where they apply:
 *  - `central.assistant`, `ai.models`, `ai.monthlyBudget`, `central.autonomy` (D639: how far Vello acts alone, per intent, with kill switches) - the platform's default and a country's own;
 *  - `central.autoRefundCaps`, `central.orderPaymentMethods`, `ai.speechEngine` - one per country (money and payment can never be
 *    loosened by a lower scope, so there is no platform or market bucket for them).
 * The screen reuses the Regras governadas flow (typed editor -> preview -> reason -> a DIFFERENT operator approves -> timeline); this
 * module owns what is specific to these keys: the typed drafts (never raw JSON), the plain-words summary of a value, the overview by
 * country, and the bucket a country has no version for yet (a first version is proposed from here).
 *
 * Nothing here decides governance or validates a value: the server's typed validators and database floors do (`/v1/config/settings/*`),
 * and the console surfaces their refusal. The helpers only keep a draft well formed while an operator edits it.
 * Neutral: keys, scopes, tokens and values are data; no country, currency or provider literal.
 */
import {
  AI_PURPOSES,
  CENTRAL_ASSISTANT_LANGUAGES,
  DEFAULT_CENTRAL_ASSISTANT,
  DEFAULT_AI_BUDGET_ALERT_PERCENTS,
  AUTONOMY_LEVELS,
  DEFAULT_CENTRAL_AUTONOMY,
  type AutonomyLevel,
  type AiPurpose,
  type CentralAssistantLanguage,
} from '@vello/config';
import type { GovernedSettingBucketView, GovernedSettingScope, GovernedSettingSummaryView } from '@vello/api-client';

/** The keys of the area, in the order the screen lists them. */
export const CENTRAL_AI_SETTING_KEYS = [
  'central.assistant',
  'central.autoRefundCaps',
  'central.orderPaymentMethods',
  'ai.models',
  'ai.speechEngine',
  'ai.monthlyBudget',
  'central.autonomy',
] as const;
export type CentralAiSettingKey = (typeof CENTRAL_AI_SETTING_KEYS)[number];

export const isCentralAiSettingKey = (key: string): key is CentralAiSettingKey => (CENTRAL_AI_SETTING_KEYS as readonly string[]).includes(key);

/** The route of the vault screen that holds the OpenRouter key and the speech endpoints (Integrações). */
export const AI_CREDENTIALS_ROUTE = 'integrations';

const asRecord = (v: unknown): Record<string, unknown> | undefined => (typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined);
const asString = (v: unknown): string => (typeof v === 'string' ? v : '');
const asNumberOrUndefined = (v: unknown): number | undefined => (typeof v === 'number' && Number.isSafeInteger(v) ? v : undefined);

// ---- central.assistant ---------------------------------------------------------------------------

export interface AssistantDraft {
  readonly displayName: string;
  readonly languages: readonly CentralAssistantLanguage[];
  readonly disclosure: Readonly<Partial<Record<CentralAssistantLanguage, string>>>;
}

const isLanguage = (v: unknown): v is CentralAssistantLanguage => (CENTRAL_ASSISTANT_LANGUAGES as readonly unknown[]).includes(v);

/** The draft the assistant editor works on: the value in force, else the standard Vello identity. */
export function assistantDraftOf(value: unknown): AssistantDraft {
  const o = asRecord(value);
  if (o === undefined) return { ...DEFAULT_CENTRAL_ASSISTANT, languages: [...DEFAULT_CENTRAL_ASSISTANT.languages] };
  const languages = Array.isArray(o.languages) ? o.languages.filter(isLanguage) : [];
  const disclosure = asRecord(o.disclosure) ?? {};
  return {
    displayName: asString(o.displayName),
    languages,
    disclosure: Object.fromEntries(languages.map((l) => [l, asString(disclosure[l])])),
  };
}

export const assistantWithName = (d: AssistantDraft, displayName: string): AssistantDraft => ({ ...d, displayName });

/**
 * Switch a language on or off. Switching it on starts its disclosure line from the standard one (when the platform has a standard
 * line in that language), so the operator edits a sentence instead of writing the legal notice from nothing; switching it off drops
 * its line (the server refuses a line for a language that is not enabled). The first language is the fallback, so the last one
 * cannot be switched off.
 */
export function assistantToggleLanguage(d: AssistantDraft, language: CentralAssistantLanguage): AssistantDraft {
  if (d.languages.includes(language)) {
    if (d.languages.length === 1) return d;
    const { [language]: _dropped, ...rest } = d.disclosure;
    return { ...d, languages: d.languages.filter((l) => l !== language), disclosure: rest };
  }
  // Keep the platform's order of languages, whatever order they were switched on in.
  const languages = CENTRAL_ASSISTANT_LANGUAGES.filter((l) => l === language || d.languages.includes(l));
  return { ...d, languages, disclosure: { ...d.disclosure, [language]: DEFAULT_CENTRAL_ASSISTANT.disclosure[language] ?? '' } };
}

export const assistantWithDisclosure = (d: AssistantDraft, language: CentralAssistantLanguage, text: string): AssistantDraft => ({ ...d, disclosure: { ...d.disclosure, [language]: text } });

// ---- central.autoRefundCaps ----------------------------------------------------------------------

export interface RefundCapsDraft {
  readonly currency: string;
  readonly perOrderMinor: bigint | undefined;
  readonly perCustomerMonthMinor: bigint | undefined;
}

/** Read the caps back from a stored value (an unreadable member is `undefined`: the editor shows the field empty). */
export function refundCapsDraftOf(value: unknown, currency: string): RefundCapsDraft {
  const o = asRecord(value);
  const minor = (v: unknown): bigint | undefined => {
    const n = asNumberOrUndefined(v);
    return n !== undefined && n >= 0 ? BigInt(n) : undefined;
  };
  return {
    currency: typeof o?.currency === 'string' && /^[A-Z]{3}$/.test(o.currency) ? o.currency : currency,
    perOrderMinor: minor(o?.perOrderMinor),
    perCustomerMonthMinor: minor(o?.perCustomerMonthMinor),
  };
}

/** The value the caps editor writes, or `undefined` while an amount is missing or too large for a safe integer. */
export function refundCapsValue(d: RefundCapsDraft): { readonly currency: string; readonly perOrderMinor: number; readonly perCustomerMonthMinor: number } | undefined {
  const safe = (n: bigint | undefined): number | undefined => (n !== undefined && n >= 0n && n <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(n) : undefined);
  const perOrderMinor = safe(d.perOrderMinor);
  const perCustomerMonthMinor = safe(d.perCustomerMonthMinor);
  if (perOrderMinor === undefined || perCustomerMonthMinor === undefined || !/^[A-Z]{3}$/.test(d.currency)) return undefined;
  return { currency: d.currency, perOrderMinor, perCustomerMonthMinor };
}

/** True when the month cap is below the order cap - the editor says so before the server has to. */
export const refundCapsInverted = (d: RefundCapsDraft): boolean => d.perOrderMinor !== undefined && d.perCustomerMonthMinor !== undefined && d.perCustomerMonthMinor < d.perOrderMinor;

// ---- central.orderPaymentMethods -----------------------------------------------------------------

/** The services a Central order can be taken for, besides a ride (which is `ride:<category>`). */
export const ORDER_NON_RIDE_SERVICES = ['food', 'send'] as const;
const RIDE_SERVICE = /^ride:[a-z][a-z0-9_-]{0,30}$/;

export type OrderPaymentServices = Readonly<Record<string, readonly string[]>>;

export function orderServicesOf(value: unknown): OrderPaymentServices {
  const services = asRecord(asRecord(value)?.services) ?? {};
  return Object.fromEntries(Object.entries(services).map(([service, list]) => [service, Array.isArray(list) ? list.filter((m): m is string => typeof m === 'string') : []]));
}

/** The value the payment-options editor writes: always settled at the end of the ride or on delivery. */
export const orderPaymentsValue = (services: OrderPaymentServices): { readonly settlement: 'AT_END'; readonly services: OrderPaymentServices } => ({
  settlement: 'AT_END',
  services: Object.fromEntries(Object.keys(services).sort().map((s) => [s, [...(services[s] ?? [])]])),
});

/** Turn one method on or off for one service, keeping the order the operator switched them on in. */
export function toggleServiceMethod(services: OrderPaymentServices, service: string, method: string): OrderPaymentServices {
  const current = services[service] ?? [];
  return { ...services, [service]: current.includes(method) ? current.filter((m) => m !== method) : [...current, method] };
}

/** Add a service row (empty: the Central offers nothing for it until a method is switched on). A malformed service id is refused. */
export function addOrderService(services: OrderPaymentServices, service: string): OrderPaymentServices {
  const known = (ORDER_NON_RIDE_SERVICES as readonly string[]).includes(service) || RIDE_SERVICE.test(service);
  return !known || service in services ? services : { ...services, [service]: [] };
}

export function removeOrderService(services: OrderPaymentServices, service: string): OrderPaymentServices {
  const { [service]: _gone, ...rest } = services;
  return rest;
}

/** `ride:go` -> the category code `go`, else `undefined` (`food`, `send`). */
export const rideCategoryOfService = (service: string): string | undefined => (service.startsWith('ride:') ? service.slice('ride:'.length) : undefined);

// ---- ai.models -----------------------------------------------------------------------------------

export interface AiModelRow {
  readonly purpose: AiPurpose;
  readonly model: string;
  readonly fallbackModel: string;
}

/** One row per purpose, in the catalogue's order; a purpose the value does not name has empty fields (= inherited or unset). */
export function aiModelRowsOf(value: unknown): readonly AiModelRow[] {
  const purposes = asRecord(asRecord(value)?.purposes) ?? {};
  return AI_PURPOSES.map((purpose) => {
    const e = asRecord(purposes[purpose]);
    return { purpose, model: asString(e?.model), fallbackModel: asString(e?.fallbackModel) };
  });
}

/** The value the models editor writes: only the purposes with a model; a blank fallback is left out. */
export function aiModelsValue(rows: readonly AiModelRow[]): { readonly provider: 'openrouter'; readonly purposes: Readonly<Record<string, { readonly model: string; readonly fallbackModel?: string }>> } {
  const purposes: Record<string, { model: string; fallbackModel?: string }> = {};
  for (const r of rows) {
    const model = r.model.trim();
    if (model === '') continue;
    const fallbackModel = r.fallbackModel.trim();
    purposes[r.purpose] = { model, ...(fallbackModel !== '' ? { fallbackModel } : {}) };
  }
  return { provider: 'openrouter', purposes };
}

export const withAiModelRow = (rows: readonly AiModelRow[], purpose: AiPurpose, patch: Partial<Pick<AiModelRow, 'model' | 'fallbackModel'>>): readonly AiModelRow[] =>
  rows.map((r) => (r.purpose === purpose ? { ...r, ...patch } : r));

// ---- ai.speechEngine -----------------------------------------------------------------------------

export type SpeechEngineDraft = { readonly engine: 'openrouter' } | { readonly engine: 'self_hosted'; readonly credentialRef: string };

export function speechEngineDraftOf(value: unknown): SpeechEngineDraft {
  const o = asRecord(value);
  return o?.engine === 'self_hosted' ? { engine: 'self_hosted', credentialRef: asString(o.credentialRef) } : { engine: 'openrouter' };
}

/** The value the engine editor writes: a self-hosted engine carries only the credential NAME (the server refuses a URL or a key). */
export const speechEngineValue = (d: SpeechEngineDraft): { readonly engine: 'openrouter' } | { readonly engine: 'self_hosted'; readonly credentialRef: string } =>
  d.engine === 'self_hosted' ? { engine: 'self_hosted', credentialRef: d.credentialRef.trim() } : { engine: 'openrouter' };

/** The credential reference a stored speech-engine value names, for the vault status read; `undefined` for OpenRouter. */
export const speechCredentialRefOf = (value: unknown): string | undefined => {
  const d = speechEngineDraftOf(value);
  return d.engine === 'self_hosted' && d.credentialRef.trim() !== '' ? d.credentialRef.trim() : undefined;
};

// ---- ai.monthlyBudget ----------------------------------------------------------------------------

export interface AiBudgetDraft {
  readonly currency: string;
  readonly amountMinor: bigint | undefined;
  readonly alertPercents: readonly number[];
}

export function aiBudgetDraftOf(value: unknown, currency: string): AiBudgetDraft {
  const o = asRecord(value);
  const amount = asNumberOrUndefined(o?.amountMinor);
  const percents = Array.isArray(o?.alertPercents) ? (o.alertPercents as unknown[]).filter((p): p is number => typeof p === 'number' && Number.isInteger(p)) : [...DEFAULT_AI_BUDGET_ALERT_PERCENTS];
  return {
    currency: typeof o?.currency === 'string' && /^[A-Z]{3}$/.test(o.currency) ? o.currency : currency,
    amountMinor: amount !== undefined && amount > 0 ? BigInt(amount) : undefined,
    alertPercents: percents,
  };
}

export function aiBudgetValue(d: AiBudgetDraft): { readonly amountMinor: number; readonly currency: string; readonly alertPercents: readonly number[] } | undefined {
  if (d.amountMinor === undefined || d.amountMinor < 1n || d.amountMinor > BigInt(Number.MAX_SAFE_INTEGER) || !/^[A-Z]{3}$/.test(d.currency)) return undefined;
  return { amountMinor: Number(d.amountMinor), currency: d.currency, alertPercents: [...d.alertPercents] };
}

/** "80, 100" typed by the operator -> the thresholds (integers, ascending, deduplicated); anything else is left for the server to refuse. */
export function parseAlertPercents(text: string): readonly number[] {
  return [...new Set(text.split(/[\s,;]+/).filter((p) => p !== '').map((p) => Number(p)))].sort((a, b) => a - b);
}

// ---- central.autonomy (D639) ---------------------------------------------------------------------

/** What the form edits: the master switch, the level of an intent with no entry of its own, one level per intent, and the two kill lists. */
export interface AutonomyDraft {
  readonly enabled: boolean;
  readonly defaultLevel: AutonomyLevel;
  readonly intents: Readonly<Record<string, AutonomyLevel>>;
  readonly disabledChannels: readonly string[];
  readonly disabledIntents: readonly string[];
}

const isAutonomyLevel = (v: unknown): v is AutonomyLevel => (AUTONOMY_LEVELS as readonly unknown[]).includes(v);
const tokensOf = (v: unknown): readonly string[] => (Array.isArray(v) ? [...new Set((v as unknown[]).filter((t): t is string => typeof t === 'string'))].sort() : []);

/** The draft the editor works on: the value in force, else the conservative default (Vello on, every intent in shadow, nothing disabled). */
export function autonomyDraftOf(value: unknown): AutonomyDraft {
  const o = asRecord(value);
  if (o === undefined) return { ...DEFAULT_CENTRAL_AUTONOMY, intents: {}, disabledChannels: [], disabledIntents: [] };
  const intents = asRecord(o.intents) ?? {};
  return {
    enabled: o.enabled !== false,
    defaultLevel: isAutonomyLevel(o.defaultLevel) ? o.defaultLevel : 'shadow',
    intents: Object.fromEntries(Object.keys(intents).sort().filter((k) => isAutonomyLevel(intents[k])).map((k) => [k, intents[k] as AutonomyLevel])),
    disabledChannels: tokensOf(o.disabledChannels),
    disabledIntents: tokensOf(o.disabledIntents),
  };
}

/** The value the form proposes (always complete: an autonomy value has no optional member). */
export const autonomyValue = (d: AutonomyDraft): AutonomyDraft => ({
  enabled: d.enabled,
  defaultLevel: d.defaultLevel,
  intents: Object.fromEntries(Object.keys(d.intents).sort().map((k) => [k, d.intents[k] as AutonomyLevel])),
  disabledChannels: [...d.disabledChannels].sort(),
  disabledIntents: [...d.disabledIntents].sort(),
});

export const autonomyWithEnabled = (d: AutonomyDraft, enabled: boolean): AutonomyDraft => ({ ...d, enabled });
export const autonomyWithDefaultLevel = (d: AutonomyDraft, defaultLevel: AutonomyLevel): AutonomyDraft => ({ ...d, defaultLevel });

/** One intent's own level; `undefined` removes the entry so the intent follows the default level again. */
export function autonomyWithIntentLevel(d: AutonomyDraft, intent: string, level: AutonomyLevel | undefined): AutonomyDraft {
  const next = { ...d.intents };
  if (level === undefined) delete next[intent];
  else next[intent] = level;
  return { ...d, intents: next };
}

/** Switch a channel or an intent off (Vello never goes above shadow there) or back on. */
export function autonomyToggleDisabled(d: AutonomyDraft, list: 'disabledChannels' | 'disabledIntents', token: string): AutonomyDraft {
  const has = d[list].includes(token);
  return { ...d, [list]: has ? d[list].filter((t) => t !== token) : [...d[list], token].sort() };
}

/**
 * The levels an intent may be given: every level when its simulation suite passes, shadow alone while it does not (the server refuses a
 * promotion without the evidence; the form simply does not offer it).
 */
export const autonomyLevelChoices = (promotable: boolean): readonly AutonomyLevel[] => (promotable ? AUTONOMY_LEVELS : ['shadow']);

// ---- the plain-words summary of a value ----------------------------------------------------------

export type CentralAiSummary =
  | { readonly kind: 'assistant'; readonly displayName: string; readonly languages: readonly string[] }
  | { readonly kind: 'refundCaps'; readonly currency: string; readonly perOrderMinor: bigint; readonly perCustomerMonthMinor: bigint }
  | { readonly kind: 'orderPayments'; readonly services: readonly { readonly service: string; readonly methods: readonly string[] }[] }
  | { readonly kind: 'aiModels'; readonly purposes: readonly { readonly purpose: string; readonly model: string; readonly fallbackModel?: string }[] }
  | { readonly kind: 'speechEngine'; readonly engine: 'openrouter' | 'self_hosted'; readonly credentialRef?: string }
  | { readonly kind: 'aiBudget'; readonly currency: string; readonly amountMinor: bigint; readonly alertPercents: readonly number[] }
  | { readonly kind: 'autonomy'; readonly enabled: boolean; readonly defaultLevel: string; readonly intents: readonly { readonly intent: string; readonly level: string }[]; readonly disabledChannels: readonly string[]; readonly disabledIntents: readonly string[] };

/** What a stored value says, structured for the screen to render in words; `undefined` when the value cannot be read as this key's. */
export function summarizeCentralAiValue(key: string, value: unknown): CentralAiSummary | undefined {
  const o = asRecord(value);
  if (o === undefined) return undefined;
  switch (key) {
    case 'central.assistant': {
      const d = assistantDraftOf(value);
      return d.displayName === '' || d.languages.length === 0 ? undefined : { kind: 'assistant', displayName: d.displayName, languages: d.languages };
    }
    case 'central.autoRefundCaps': {
      const d = refundCapsDraftOf(value, '');
      return d.perOrderMinor === undefined || d.perCustomerMonthMinor === undefined || d.currency === '' ? undefined : { kind: 'refundCaps', currency: d.currency, perOrderMinor: d.perOrderMinor, perCustomerMonthMinor: d.perCustomerMonthMinor };
    }
    case 'central.orderPaymentMethods': {
      const services = orderServicesOf(value);
      return { kind: 'orderPayments', services: Object.keys(services).sort().map((service) => ({ service, methods: services[service] ?? [] })) };
    }
    case 'ai.models':
      return { kind: 'aiModels', purposes: aiModelRowsOf(value).filter((r) => r.model !== '').map((r) => ({ purpose: r.purpose, model: r.model, ...(r.fallbackModel !== '' ? { fallbackModel: r.fallbackModel } : {}) })) };
    case 'ai.speechEngine': {
      const d = speechEngineDraftOf(value);
      return { kind: 'speechEngine', engine: d.engine, ...(d.engine === 'self_hosted' ? { credentialRef: d.credentialRef } : {}) };
    }
    case 'ai.monthlyBudget': {
      const d = aiBudgetDraftOf(value, '');
      return d.amountMinor === undefined || d.currency === '' ? undefined : { kind: 'aiBudget', currency: d.currency, amountMinor: d.amountMinor, alertPercents: d.alertPercents };
    }
    case 'central.autonomy': {
      const d = autonomyDraftOf(value);
      return { kind: 'autonomy', enabled: d.enabled, defaultLevel: d.defaultLevel, intents: Object.keys(d.intents).sort().map((intent) => ({ intent, level: d.intents[intent] as string })), disabledChannels: d.disabledChannels, disabledIntents: d.disabledIntents };
    }
    default:
      return undefined;
  }
}

// ---- the overview by country ---------------------------------------------------------------------

export type OverviewCellState = 'NOT_ALLOWED' | 'UNSET' | 'DEFAULT' | 'SET' | 'INHERITED' | 'FAIL_CLOSED';

export interface OverviewCell {
  readonly key: CentralAiSettingKey;
  readonly state: OverviewCellState;
  readonly version?: number;
  readonly value?: unknown;
  readonly pending: number;
}

export interface OverviewRow {
  readonly scope: Extract<GovernedSettingScope, 'platform' | 'country'>;
  /** `''` for the platform, `XX` for a country. */
  readonly scopeRef: string;
  readonly cells: readonly OverviewCell[];
}

/**
 * The area as a grid: one row per place the settings apply (the platform, then each country), one cell per key. A cell says whether
 * the key exists at that place at all (`NOT_ALLOWED`: money and payment have no platform bucket), whether a version governs it
 * (`SET`), whether the platform's version governs the country because the country has none of its own (`INHERITED`: for a key
 * that is platform-scoped and OVERRIDABLE, `version` and `value` are the platform's), whether only the key's default does (`DEFAULT`), whether nothing does (`UNSET`: for money and payment, no automatic money
 * and no payment option are offered), or whether what is stored cannot be verified (`FAIL_CLOSED`).
 */
export function centralAiOverview(summaries: readonly GovernedSettingSummaryView[], countries: readonly string[]): readonly OverviewRow[] {
  const byKey = new Map(summaries.filter((s) => isCentralAiSettingKey(s.key)).map((s) => [s.key, s]));
  const known = new Set<string>(countries.map((c) => c.toUpperCase()));
  // A country that holds a version but is not in the market registry still gets its row.
  for (const s of byKey.values()) for (const b of s.buckets) if (b.scope === 'country' && b.scopeRef !== '') known.add(b.scopeRef);
  const places: readonly { scope: 'platform' | 'country'; scopeRef: string }[] = [{ scope: 'platform', scopeRef: '' }, ...[...known].sort().map((scopeRef) => ({ scope: 'country' as const, scopeRef }))];
  return places.map((place) => ({
    ...place,
    cells: CENTRAL_AI_SETTING_KEYS.map((key): OverviewCell => {
      const summary = byKey.get(key);
      if (summary === undefined || !summary.allowedScopes.includes(place.scope)) return { key, state: 'NOT_ALLOWED', pending: 0 };
      const bucket = summary.buckets.find((b) => b.scope === place.scope && b.scopeRef === place.scopeRef);
      const pending = bucket?.pending.length ?? 0;
      if (bucket?.current === undefined) {
        // A country with no version of its own is governed by the platform's, when the key has a platform scope and is OVERRIDABLE.
        const platform =
          place.scope === 'country' && summary.mode === 'OVERRIDABLE' && summary.allowedScopes.includes('platform')
            ? summary.buckets.find((b) => b.scope === 'platform' && b.scopeRef === '')?.current
            : undefined;
        if (platform !== undefined) {
          const unverified = platform.origin === 'UNVERIFIED' || summary.buckets.some((b) => b.scope === 'platform' && b.scopeRef === '' && b.failClosed === true);
          return unverified
            ? { key, state: 'FAIL_CLOSED', version: platform.version, pending }
            : { key, state: 'INHERITED', version: platform.version, value: platform.value, pending };
        }
        return { key, state: summary.defaultValue !== undefined ? 'DEFAULT' : 'UNSET', pending };
      }
      if (bucket.failClosed === true || bucket.current.origin === 'UNVERIFIED') return { key, state: 'FAIL_CLOSED', version: bucket.current.version, pending };
      return { key, state: 'SET', version: bucket.current.version, value: bucket.current.value, pending };
    }),
  }));
}

/**
 * The bucket of a place that has no version of a key yet, so an operator can propose its first one from the screen. It is exactly what
 * the server would report for an empty bucket (`latestVersion` 0, nothing in force, nothing pending); a market bucket is never made. A STATE bucket (`region`, ADR-0124) is made for the state the top selector is on.
 */
export function emptyBucket(scope: 'platform' | 'country' | 'region', scopeRef: string, defaultValue?: unknown): GovernedSettingBucketView {
  return {
    scope,
    scopeRef,
    latestVersion: 0,
    base: defaultValue !== undefined ? { source: 'DEFAULT', value: defaultValue } : { source: 'DEFAULT' },
    scheduledVersions: [],
    pending: [],
  };
}

/** The distinct, upper-case countries of the registered markets, sorted. */
export function countriesOfMarkets(markets: readonly { readonly countryCode: string }[]): readonly string[] {
  return [...new Set(markets.map((m) => m.countryCode.toUpperCase()).filter((c) => /^[A-Z]{2}$/.test(c)))].sort();
}
